import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);

  const persistToken = (token) => {
    if (token) localStorage.setItem("stupeco_session", token);
  };
  const clearToken = () => localStorage.removeItem("stupeco_session");

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback handle it first.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persistToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, consents = null) => {
    const { data } = await api.post("/auth/register", { name, email, password, consents });
    persistToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const exchangeGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId }, { headers: { "X-Session-ID": sessionId } });
    persistToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    clearToken();
    setUser(false);
  };

  const resendVerification = async () => {
    const { data } = await api.post("/auth/resend-verification");
    return data;
  };

  const updateProfile = async (name) => {
    const { data } = await api.patch("/me/profile", { name });
    setUser(data.user);
    return data.user;
  };

  const updatePreferences = async (preferences) => {
    const { data } = await api.patch("/me/preferences", { preferences });
    setUser((u) => (u && typeof u === "object" ? { ...u, preferences: data.preferences } : u));
    return data.preferences;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        register,
        logout,
        exchangeGoogleSession,
        checkAuth,
        resendVerification,
        updateProfile,
        updatePreferences,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
